import React, { useState, useMemo } from 'react';
import { StickyNote, Plus, X, Pin, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useApp } from '../context/AppContext';
import './DayNotesPanel.css';

// Date-level notes shown on the Dispatch page for the currently selected day.
// Notes can be marked as "pinned" (highlighted) to call out things like
// "Ashanti leaves at 3pm" or "Move the Henderson appointment".
function DayNotesPanel({ selectedDate }) {
  const { getDayNotes, addDayNote, updateDayNote, deleteDayNote } = useApp();
  const [collapsed, setCollapsed] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftType, setDraftType] = useState('general');
  const [saving, setSaving] = useState(false);

  const notes = useMemo(() => getDayNotes(selectedDate), [getDayNotes, selectedDate]);
  const pinned = notes.filter(n => n.type === 'pin');
  const general = notes.filter(n => n.type !== 'pin');

  const handleAdd = async () => {
    const text = draft.trim();
    if (!text) return;
    setSaving(true);
    try {
      await addDayNote({ date: selectedDate, text, type: draftType });
      setDraft('');
      setDraftType('general');
    } finally {
      setSaving(false);
    }
  };

  const togglePin = (note) => {
    updateDayNote(note.id, { type: note.type === 'pin' ? 'general' : 'pin' });
  };

  const formattedDate = (() => {
    try { return format(parseISO(selectedDate), 'EEEE, MMM d'); }
    catch { return selectedDate; }
  })();

  return (
    <div className="day-notes-panel">
      <button className="day-notes-header" onClick={() => setCollapsed(c => !c)}>
        <StickyNote size={18} />
        <span className="day-notes-title">Day Notes</span>
        <span className="day-notes-date">— {formattedDate}</span>
        {notes.length > 0 && (
          <span className="day-notes-count">{notes.length}</span>
        )}
        {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
      </button>

      {!collapsed && (
        <div className="day-notes-body">
          {pinned.length > 0 && (
            <div className="day-notes-pinned">
              {pinned.map(note => (
                <div key={note.id} className="day-note pinned">
                  <Pin size={14} />
                  <span className="day-note-text">{note.text}</span>
                  <button className="day-note-action" onClick={() => togglePin(note)} title="Unpin">
                    <Pin size={14} />
                  </button>
                  <button className="day-note-action" onClick={() => deleteDayNote(note.id)} title="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {general.length > 0 && (
            <div className="day-notes-list">
              {general.map(note => (
                <div key={note.id} className="day-note">
                  <span className="day-note-text">{note.text}</span>
                  <button className="day-note-action" onClick={() => togglePin(note)} title="Pin to top">
                    <Pin size={14} />
                  </button>
                  <button className="day-note-action" onClick={() => deleteDayNote(note.id)} title="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {notes.length === 0 && (
            <p className="day-notes-empty">No notes yet for this day. Add one below.</p>
          )}

          <div className="day-notes-composer">
            <input
              type="text"
              placeholder="e.g. Ashanti leaves at 3pm — needs early route"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
              disabled={saving}
            />
            <label className="day-notes-pin-toggle">
              <input
                type="checkbox"
                checked={draftType === 'pin'}
                onChange={(e) => setDraftType(e.target.checked ? 'pin' : 'general')}
              />
              <Pin size={14} /> Pin
            </label>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleAdd}
              disabled={!draft.trim() || saving}
            >
              <Plus size={14} /> Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DayNotesPanel;
